import { useEffect, useState } from 'react'
import { Play, RefreshCw, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useSaveWebbillSyncConfig,
  useTriggerWebbillSync,
  useWebbillSyncConfig,
  useWebbillSyncHistory,
} from '../hooks/use-webbill-sync'
import type { WebbillSyncConfigRequest } from '@/api/endpoints/webbill-sync'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { useCategories } from '@/features/categories/hooks/use-categories'

// Example connection URL shown as an input placeholder; not user-facing copy.
const BASE_URL_PLACEHOLDER = 'http://10.5.1.21'

export function WebbillSyncPage() {
  const { t } = useTranslation('webbill-sync')
  const { data: config, isLoading: configLoading } = useWebbillSyncConfig()
  const { data: historyData, refetch: refetchHistory } = useWebbillSyncHistory()
  const saveMutation = useSaveWebbillSyncConfig()
  const triggerMutation = useTriggerWebbillSync()

  const { data: categoriesData } = useCategories()
  const categories = categoriesData?.items ?? []

  const [form, setForm] = useState<WebbillSyncConfigRequest>({
    enabled: false,
    baseUrl: '',
    clientId: '',
    idsToken: '',
    datasheetToken: '',
    cronExpression: '0 0 3 * * *',
    newProductsActive: false,
    deactivateRemoved: false,
    includeImages: false,
    batchSize: 5,
    batchDelayMs: 200,
    requestDelayMs: 50,
    defaultCategoryCode: null,
  })

  useEffect(() => {
    if (config?.configured) {
      setForm({
        enabled: config.enabled ?? false,
        baseUrl: config.baseUrl ?? '',
        clientId: config.clientId ?? '',
        idsToken: config.idsToken ?? '',
        datasheetToken: config.datasheetToken ?? '',
        cronExpression: config.cronExpression ?? '0 0 3 * * *',
        newProductsActive: config.newProductsActive ?? false,
        deactivateRemoved: config.deactivateRemoved ?? false,
        includeImages: config.includeImages ?? false,
        batchSize: config.batchSize ?? 5,
        batchDelayMs: config.batchDelayMs ?? 200,
        requestDelayMs: config.requestDelayMs ?? 50,
        defaultCategoryCode: config.defaultCategoryCode ?? null,
      })
    }
  }, [config])

  // Auto-refresh history when a sync is running
  const hasRunning = historyData?.items.some((h) => h.status === 'running')
  useEffect(() => {
    if (!hasRunning) return
    const interval = setInterval(() => refetchHistory(), 5000)
    return () => clearInterval(interval)
  }, [hasRunning, refetchHistory])

  const handleSave = () => {
    saveMutation.mutate(form, {
      onSuccess: () => toast.success(t('toast.configSaved')),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : t('toast.saveFailed')),
    })
  }

  const handleTrigger = () => {
    triggerMutation.mutate(undefined, {
      onSuccess: (data) => {
        toast.success(
          t('toast.syncComplete', {
            created: data.created,
            updated: data.updated,
          }),
        )
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : ''
        if (msg.includes('409') || msg.includes('sync_already_running')) {
          toast.warning(t('toast.alreadyRunning'))
        } else {
          toast.error(t('toast.syncFailed'))
        }
      },
    })
  }

  const updateField = <TKey extends keyof WebbillSyncConfigRequest>(
    key: TKey,
    value: WebbillSyncConfigRequest[TKey],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  return (
    <PermissionGate action={ACTIONS.CATALOG_IMPORT}>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground mt-2">{t('subtitle')}</p>
        </div>

        {/* Settings Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('settings.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
              className="space-y-6"
            >
              {/* Connection */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('settings.connection')}
                </h3>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="baseUrl">{t('settings.baseUrl')}</Label>
                    <Input
                      id="baseUrl"
                      value={form.baseUrl}
                      onChange={(e) => updateField('baseUrl', e.target.value)}
                      placeholder={BASE_URL_PLACEHOLDER}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="clientId">{t('settings.clientId')}</Label>
                    <Input
                      id="clientId"
                      value={form.clientId}
                      onChange={(e) => updateField('clientId', e.target.value)}
                      placeholder="30"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('settings.clientIdHint')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cronExpression">
                      {t('settings.cronExpression')}
                    </Label>
                    <Input
                      id="cronExpression"
                      value={form.cronExpression}
                      onChange={(e) =>
                        updateField('cronExpression', e.target.value)
                      }
                      placeholder="0 0 3 * * *"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('settings.cronHint')}
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="idsToken">{t('settings.idsToken')}</Label>
                    <Input
                      id="idsToken"
                      value={form.idsToken}
                      onChange={(e) => updateField('idsToken', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="datasheetToken">
                      {t('settings.datasheetToken')}
                    </Label>
                    <Input
                      id="datasheetToken"
                      value={form.datasheetToken}
                      onChange={(e) =>
                        updateField('datasheetToken', e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('settings.options')}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <Label>{t('settings.enabled')}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('settings.enabledHint')}
                      </p>
                    </div>
                    <Switch
                      checked={form.enabled}
                      onCheckedChange={(v) => updateField('enabled', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <Label>{t('settings.newProductsActive')}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('settings.newProductsActiveHint')}
                      </p>
                    </div>
                    <Switch
                      checked={form.newProductsActive}
                      onCheckedChange={(v) =>
                        updateField('newProductsActive', v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <Label>{t('settings.deactivateRemoved')}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('settings.deactivateRemovedHint')}
                      </p>
                    </div>
                    <Switch
                      checked={form.deactivateRemoved}
                      onCheckedChange={(v) =>
                        updateField('deactivateRemoved', v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <Label>{t('settings.includeImages')}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t('settings.includeImagesHint')}
                      </p>
                    </div>
                    <Switch
                      checked={form.includeImages}
                      onCheckedChange={(v) => updateField('includeImages', v)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="defaultCategory">
                    {t('settings.defaultCategory')}
                  </Label>
                  <Select
                    value={form.defaultCategoryCode ?? '__general__'}
                    onValueChange={(v) =>
                      updateField(
                        'defaultCategoryCode',
                        v === '__general__' ? null : v,
                      )
                    }
                  >
                    <SelectTrigger id="defaultCategory">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__general__">
                        {t('settings.generalCategory')}
                      </SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.code} value={cat.code}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('settings.defaultCategoryHint')}
                  </p>
                </div>
              </div>

              {/* Throttle */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('settings.throttle')}
                </h3>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="batchSize">{t('settings.batchSize')}</Label>
                    <Input
                      id="batchSize"
                      type="number"
                      min={1}
                      max={50}
                      value={form.batchSize}
                      onChange={(e) =>
                        updateField('batchSize', parseInt(e.target.value) || 5)
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('settings.batchSizeHint')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="batchDelayMs">
                      {t('settings.batchDelayMs')}
                    </Label>
                    <Input
                      id="batchDelayMs"
                      type="number"
                      min={0}
                      max={5000}
                      value={form.batchDelayMs}
                      onChange={(e) =>
                        updateField(
                          'batchDelayMs',
                          parseInt(e.target.value) || 0,
                        )
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('settings.batchDelayMsHint')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="requestDelayMs">
                      {t('settings.requestDelayMs')}
                    </Label>
                    <Input
                      id="requestDelayMs"
                      type="number"
                      min={0}
                      max={2000}
                      value={form.requestDelayMs}
                      onChange={(e) =>
                        updateField(
                          'requestDelayMs',
                          parseInt(e.target.value) || 0,
                        )
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('settings.requestDelayMsHint')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? (
                    <Spinner className="w-4 h-4 mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.save')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleTrigger}
                  disabled={triggerMutation.isPending || !config?.configured}
                >
                  {triggerMutation.isPending ? (
                    <Spinner className="w-4 h-4 mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.syncNow')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* History Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('history.title')}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetchHistory()}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!historyData?.items.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('history.empty')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4">{t('history.status')}</th>
                      <th className="pb-2 pr-4">{t('history.trigger')}</th>
                      <th className="pb-2 pr-4">{t('history.started')}</th>
                      <th className="pb-2 pr-4">{t('history.duration')}</th>
                      <th className="pb-2 pr-4 text-right">
                        {t('history.created')}
                      </th>
                      <th className="pb-2 pr-4 text-right">
                        {t('history.updated')}
                      </th>
                      <th className="pb-2 pr-4 text-right">
                        {t('history.deactivated')}
                      </th>
                      <th className="pb-2 text-right">{t('history.errors')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.items.map((item) => (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="py-2 pr-4">
                          <StatusBadge status={item.status} t={t} />
                        </td>
                        <td className="py-2 pr-4">{item.triggerType}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {formatRelativeTime(item.startedAt)}
                        </td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {formatDuration(item.startedAt, item.finishedAt)}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {item.createdCount}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {item.updatedCount}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {item.deactivatedCount}
                        </td>
                        <td className="py-2 text-right">
                          <span
                            className={
                              item.errorCount > 0 ? 'text-destructive' : ''
                            }
                          >
                            {item.errorCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}

function StatusBadge({
  status,
  t,
}: {
  status: string
  t: (key: string) => string
}) {
  const variant =
    status === 'success'
      ? 'default'
      : status === 'running'
        ? 'secondary'
        : 'destructive'
  return <Badge variant={variant}>{t(`history.statusLabel.${status}`)}</Badge>
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatDuration(start: string, end?: string): string {
  if (!end) return '—'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  const secs = Math.floor(ms / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const remSecs = secs % 60
  return `${mins}m ${remSecs}s`
}
